import { useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import type { LandmarkDeviationWithCoords } from '@/types/visualization';
import {
  SCORE_COLORS,
  SCORE_THRESHOLDS,
  PARTICLE_SYSTEM,
  COMMON_COLORS,
  RENDERING,
  isLandmarkVisible,
  addHexAlpha,
} from '@/utils/visualizationConstants';

interface ParticleAuraOverlayProps {
  canvasRef?: React.RefObject<HTMLCanvasElement>;
  landmarks: Array<{ x: number; y: number; visibility?: number }>;
  landmarkDeviations?: LandmarkDeviationWithCoords[];
  /** Width of the actual video render area */
  width: number;
  /** Height of the actual video render area */
  height: number;
  /** X offset from canvas left edge to the video render area (pillarbox offset) */
  offsetX?: number;
  /** Y offset from canvas top edge to the video render area (letterbox offset) */
  offsetY?: number;
  /** Full container width — canvas is sized to this */
  containerWidth?: number;
  /** Full container height — canvas is sized to this */
  containerHeight?: number;
  intensity: number; // 0-100
  currentFrame: number;
  fallbackScore?: number | null; // Frame-level score to use when no per-landmark data
  className?: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  landmarkId: number;
}

// Note: MAX_PARTICLES now defined in PARTICLE_SYSTEM constants

// Particle spawn rate based on intensity (particles per landmark per frame)
const getSpawnRate = (intensity: number): number => {
  if (intensity < 30) return 0.2;
  if (intensity < 60) return 0.5;
  return 1.0;
};

// Get particle size based on intensity
const getParticleSize = (intensity: number): number => {
  return 2 + (intensity / 100) * 4; // 2-6px
};

// Get particle lifetime based on intensity (frames)
const getParticleLifetime = (intensity: number): number => {
  return 30 + (intensity / 100) * 30; // 30-60 frames
};

// Get color based on deviation score
// Returns neutral grey for landmarks without score data (not part of annotated regions)
const getScoreColor = (score: number | null): string => {
  if (score === null) return SCORE_COLORS.NEUTRAL; // grey for unscored landmarks
  if (score >= SCORE_THRESHOLDS.GOOD) return SCORE_COLORS.GOOD; // teal-500
  if (score >= SCORE_THRESHOLDS.MEDIUM) return SCORE_COLORS.MEDIUM; // amber-400
  return SCORE_COLORS.POOR; // red-500
};

/**
 * ParticleAuraOverlay renders animated particles around athlete landmarks
 * based on alignment quality. Particles emanate outward from each landmark
 * with color-coding based on deviation scores.
 */
function ParticleAuraOverlay({
  canvasRef: externalCanvasRef,
  landmarks,
  landmarkDeviations = [],
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  containerWidth,
  containerHeight,
  intensity,
  currentFrame,
  fallbackScore,
  className,
}: ParticleAuraOverlayProps) {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const canvasW = containerWidth ?? width;
  const canvasH = containerHeight ?? height;
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastFrameRef = useRef<number>(currentFrame);

  // Map landmark ID to its current deviation score
  // Returns fallbackScore if no deviation data exists, or null if no fallback
  const getLandmarkScore = (landmarkId: number): number | null => {
    const deviation = (landmarkDeviations ?? []).find(
      d => d.frame_index === currentFrame && d.landmark_id === landmarkId
    );
    if (deviation) return deviation.deviation_score;
    // Use fallback frame score if available
    if (fallbackScore !== null && fallbackScore !== undefined) {
      return fallbackScore;
    }
    return null;
  };

  // Spawn new particles at landmark positions
  const spawnParticles = () => {
    const baseSpawnRate = getSpawnRate(intensity);
    const baseParticleSize = getParticleSize(intensity);
    const baseParticleLifetime = getParticleLifetime(intensity);

    landmarks.forEach((landmark, index) => {
      // Skip invisible landmarks
      if (!isLandmarkVisible(landmark)) return;

      const score = getLandmarkScore(index);

      // Modulate particle behavior based on score
      // Poor scores = more/faster/larger particles to draw attention
      // Good scores = fewer/slower/smaller particles (calm)
      // Neutral/unscored = minimal particles
      let spawnRateMultiplier = 1.0;
      let sizeMultiplier = 1.0;
      let speedMultiplier = 1.0;
      let lifetimeMultiplier = 1.0;

      if (score === null) {
        // Neutral/unscored landmarks: minimal particles
        spawnRateMultiplier = 0.3;
        sizeMultiplier = 0.7;
        speedMultiplier = 0.6;
        lifetimeMultiplier = 0.8;
      } else if (score >= SCORE_THRESHOLDS.GOOD) {
        // Good scores: calm, subtle particles
        spawnRateMultiplier = 0.6;
        sizeMultiplier = 0.8;
        speedMultiplier = 0.7;
        lifetimeMultiplier = 1.0;
      } else if (score >= SCORE_THRESHOLDS.MEDIUM) {
        // Medium scores: moderate activity
        spawnRateMultiplier = 1.0;
        sizeMultiplier = 1.0;
        speedMultiplier = 1.0;
        lifetimeMultiplier = 1.0;
      } else {
        // Poor scores: aggressive particles to draw attention
        spawnRateMultiplier = 1.5;
        sizeMultiplier = 1.3;
        speedMultiplier = 1.4;
        lifetimeMultiplier = 1.2;
      }

      const spawnRate = baseSpawnRate * spawnRateMultiplier;
      const particleSize = baseParticleSize * sizeMultiplier;
      const particleLifetime = baseParticleLifetime * lifetimeMultiplier;

      // Randomly spawn particles based on adjusted spawn rate
      if (Math.random() > spawnRate) return;

      // Check if we've hit the max particle limit
      if (particlesRef.current.length >= PARTICLE_SYSTEM.MAX_PARTICLES) return;

      const color = getScoreColor(score);

      // Convert normalized coordinates to pixel coordinates (apply video render area offset)
      const px = landmark.x * width + offsetX;
      const py = landmark.y * height + offsetY;

      // Generate random velocity (outward in all directions)
      const angle = Math.random() * Math.PI * 2;
      const baseSpeed = 0.5 + Math.random() * 1.5; // 0.5-2.0 pixels per frame
      const speed = baseSpeed * speedMultiplier;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;

      // Create new particle
      const particle: Particle = {
        x: px,
        y: py,
        vx,
        vy,
        life: particleLifetime,
        maxLife: particleLifetime,
        size: particleSize * (0.8 + Math.random() * 0.4), // Add some size variation
        color,
        landmarkId: index,
      };

      particlesRef.current.push(particle);
    });
  };

  // Update particle positions and remove dead particles
  const updateParticles = () => {
    particlesRef.current = particlesRef.current.filter(particle => {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Decay life
      particle.life -= 1;

      // Apply slight drag
      particle.vx *= PARTICLE_SYSTEM.DRAG_COEFFICIENT;
      particle.vy *= PARTICLE_SYSTEM.DRAG_COEFFICIENT;

      // Keep particle if still alive
      return particle.life > 0;
    });
  };

  // Render particles on canvas
  const renderParticles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      // Clear canvas (full container, not just render area)
      ctx.clearRect(0, 0, canvasW, canvasH);

      // Draw each particle
    particlesRef.current.forEach(particle => {
      // Calculate opacity based on remaining life (fade out)
      const lifeRatio = particle.life / particle.maxLife;
      const opacity = Math.min(1, lifeRatio * 1.5) * (intensity / 100);

      // Draw particle as a circle with glow
      ctx.save();
      ctx.globalAlpha = opacity;

      // Outer glow
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size * 1.5, 0, Math.PI * 2);
      ctx.fillStyle = addHexAlpha(particle.color, PARTICLE_SYSTEM.GLOW_HEX_ALPHA);
      ctx.fill();

      // Inner core
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = particle.color;
      ctx.fill();

      // Optional: Add bright center
      if (intensity > PARTICLE_SYSTEM.BRIGHT_CENTER_INTENSITY) {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * PARTICLE_SYSTEM.BRIGHT_CENTER_SIZE_RATIO, 0, Math.PI * 2);
        ctx.fillStyle = COMMON_COLORS.WHITE;
        ctx.fill();
      }

      ctx.restore();
    });
    } catch (error) {
      console.error('[ParticleAuraOverlay] Canvas drawing error:', error);
    }
  };

  // Start/stop animation loop
  useEffect(() => {
    if (!canvasRef.current || !landmarks || landmarks.length === 0) return;

    // Skip animation when intensity is too low to save CPU cycles
    if (intensity < RENDERING.MIN_INTENSITY) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasW, canvasH);
      }
      particlesRef.current = [];
      return;
    }

    // Clear particles when frame changes significantly (video seek)
    if (Math.abs(currentFrame - lastFrameRef.current) > PARTICLE_SYSTEM.FRAME_JUMP_THRESHOLD) {
      particlesRef.current = [];
    }
    lastFrameRef.current = currentFrame;

    // Animation loop
    const animate = () => {
      try {
        // Spawn new particles
        spawnParticles();

        // Update existing particles
        updateParticles();

        // Render particles
        renderParticles();
      } catch (error) {
        console.error('[ParticleAuraOverlay] Animation error:', error);
      }

      // Continue animation
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Start animation loop
    animate();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef, landmarks, landmarkDeviations, width, height, offsetX, offsetY, canvasW, canvasH, intensity, currentFrame]);

  // Cleanup particles when component unmounts
  useEffect(() => {
    return () => {
      particlesRef.current = [];
    };
  }, []);

  // Always render canvas - the useEffect handles drawing to whichever ref is used
  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%' }}
      aria-label="Particle aura overlay"
    />
  );
}

export default ParticleAuraOverlay;
